'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */

import { useCtaUrl } from '@hyphengroup/cms-components/components/ApplyCta';
import { resolveAttributeRow } from '@hyphengroup/cms-components/components/ProductDetails/utils/resolveAttributeRow/resolveAttributeRow';
import { RichText } from '@hyphengroup/cms-components/components/RichText';
import { ATTR_NAMES } from '@hyphengroup/cms-components/types/attrNames';
import classNames from 'classnames';
import { FC } from 'react';
import { FlexibleHeroBlockBreadcrumb } from '../FlexibleHeroBlockBreadcrumb';
import { FlexibleHeroBlockSection } from '../FlexibleHeroBlockSection';
import { mapProductHeroData } from '../productHero';
import { FlexibleHeroBlockProps } from '../types';
import { useFlexibleHeroBlockApplyClick } from '../useFlexibleHeroBlockApplyClick';
import { useFlexibleHeroBlockTimestamp } from '../useFlexibleHeroBlockTimestamp';
import {
  getAlignmentClasses,
  getProductCallToAction,
  getProductCtaLinkOverride,
  getTextClasses,
} from '../utils';
import {
  FlexibleHeroBlockStkBkaAttributeChips,
  FlexibleHeroBlockStkBkaChipRow,
} from './components/FlexibleHeroBlockStkBkaAttributeChips';
import { FlexibleHeroBlockStkBkaHeading } from './components/FlexibleHeroBlockStkBkaHeading';
import { FlexibleHeroBlockStkBkaSideCard } from './components/FlexibleHeroBlockStkBkaSideCard';
import { tr } from '$/components/cms/helpers';
import { useClientContext } from '$/modules/context/client';

/**
 * Attributes shown as labelled chip rows under the hero text, in design order,
 * each row with its own chip colour. Investments products carry them; a
 * product without one (every Banking product today) shows no row for it.
 */
const STK_BKA_CHIP_ROWS: ReadonlyArray<
  Pick<FlexibleHeroBlockStkBkaChipRow, 'attributeKey' | 'chipTextClassName'>
> = [
  {
    attributeKey: ATTR_NAMES.TRADING_PLATFORMS,
    chipTextClassName: 'text-highlights/orange-dark',
  },
  {
    attributeKey: ATTR_NAMES.INVESTMENT_PRODUCTS_OFFERED,
    chipTextClassName: 'text-text/primary',
  },
  {
    attributeKey: ATTR_NAMES.INVESTMENT_PAYMENT_NETWORK,
    chipTextClassName: 'text-text/accent',
  },
];

/** Text-column elements once the layout is two columns. */
const TEXT_COLUMN_CLASSES = classNames('md:col-start-1');

/**
 * Text-column rows under the heading (body copy, chips). From `md` the grid
 * has no row gap — the side card spans three explicit rows, and a row with
 * nothing in it would still bring its gap — so the space sits on the rows
 * that render.
 */
const TEXT_COLUMN_NEXT_ROW_CLASSES = classNames('md:col-start-1 md:mt-6');

/**
 * Product hero of the Investments (STK) and Banking (BKA) PDPs (CMS-1934): a
 * static hero built from the linked product — no card art and no calculator.
 *
 * It renders on a page only, so it goes two-column at `md` and does not read
 * `wideLayoutBreakpoint`: the #usePDP modal renders the default hero (it
 * passes no vertical, and it opens for credit cards only).
 */
export const FlexibleHeroBlockStkBka: FC<FlexibleHeroBlockProps> = ({
  id,
  backgroundImageDesktop,
  backgroundImageMobile,
  bodyCopy,
  headline,
  keyStats,
  overline,
  product,
  productCallToAction,
  showBreadcrumbs,
  showTimestamp,
  textAlign,
  theme,
  ContentContainer,
  isPageHeadline = true,
  ctaLinkOverride: hostCtaLinkOverride,
}) => {
  const { brand, lang } = useClientContext();
  const handleApplyClick = useFlexibleHeroBlockApplyClick(product);
  const timestamp = useFlexibleHeroBlockTimestamp(showTimestamp, lang);
  const productHeroData = mapProductHeroData({
    brand,
    bodyCopyOverride: bodyCopy,
    headlineOverride: headline,
    keyStats,
    lang,
    product,
  });
  const applyLabel = tr('apply_now', lang);
  const productHref = useCtaUrl({
    product,
    ctaLinkOverride: hostCtaLinkOverride,
  });
  // The same door as the default Product hero's CTA: no URL, no CTA.
  const cta = getProductCallToAction(
    product,
    productCallToAction,
    applyLabel,
    productHref,
    hostCtaLinkOverride,
  );
  // ApplyCtaLink in the side card resolves the URL itself, with the same
  // order as the CTA above: the host's link, else the block's authored one
  // (CMS-2043).
  const ctaLinkOverride =
    hostCtaLinkOverride ?? getProductCtaLinkOverride(productCallToAction);
  // Decorative: the product's name is the hero's headline right next to it.
  const logo = product?.product_image
    ? { src: product.product_image, alt: '' }
    : undefined;
  const { promoCode } = productHeroData;
  // Everything in the card comes from the linked product. Without one (an
  // unlinked Product hero), or with nothing of it to show, there is no card
  // and the hero is one column.
  const hasSideCard = Boolean(
    product &&
      (logo || promoCode || productHeroData.keyStats.length > 0 || cta),
  );
  const chipRows = STK_BKA_CHIP_ROWS.flatMap(
    ({ attributeKey, chipTextClassName }) => {
      const row = resolveAttributeRow(product, { attributeKey });
      return row?.kind === 'list' ? [{ ...row, chipTextClassName }] : [];
    },
  );
  const textClasses = getTextClasses(theme);
  const { isCenter } = getAlignmentClasses(textAlign);
  const headingId = productHeroData.headline
    ? `flexible-hero-${id}`
    : undefined;

  return (
    <FlexibleHeroBlockSection
      theme={theme}
      backgroundImageDesktop={backgroundImageDesktop}
      backgroundImageMobile={backgroundImageMobile}
      ContentContainer={ContentContainer}
      headingId={headingId}
    >
      {/* One column while stacked (heading, side card, chips — no body copy
          below `md`, as in the design and the default Product hero); from
          `md` the text column and the 304px side card sit side by side, the
          card spanning the text column's rows. */}
      <div
        className={classNames(
          'grid w-full grid-cols-1 gap-6 md:gap-y-0',
          hasSideCard &&
            'md:grid-cols-[minmax(0,1fr)_304px] md:grid-rows-[auto_auto_1fr] md:gap-x-spacing/gap/section-y-lg',
          isCenter ? 'justify-items-center text-center' : 'text-left',
        )}
        data-component="flexible-hero-block-stk-bka"
      >
        <div className={TEXT_COLUMN_CLASSES}>
          <FlexibleHeroBlockStkBkaHeading
            breadcrumb={showBreadcrumbs && <FlexibleHeroBlockBreadcrumb />}
            overline={overline}
            headline={productHeroData.headline}
            headingId={headingId}
            isPageHeadline={isPageHeadline}
            timestamp={timestamp}
            textClasses={textClasses}
            isCenter={isCenter}
          />
        </div>

        {product && hasSideCard && (
          <FlexibleHeroBlockStkBkaSideCard
            className="md:col-start-2 md:row-span-3 md:row-start-1 md:self-start"
            logo={logo}
            promoCode={promoCode}
            product={product}
            ctaLabel={cta?.label}
            ctaLinkOverride={ctaLinkOverride}
            onApplyClick={handleApplyClick}
            keyStats={productHeroData.keyStats}
            lang={lang}
          />
        )}

        {productHeroData.bodyCopy && (
          <RichText
            className={classNames(
              'mhg-alpha-typography-body-2-relaxed lg:mhg-alpha-typography-body-1-relaxed hidden w-full md:block md:max-w-general/6-col-width',
              TEXT_COLUMN_NEXT_ROW_CLASSES,
              textClasses.body,
              isCenter ? 'mx-auto text-center' : 'text-left',
            )}
            baseTextStyle="none"
          >
            {productHeroData.bodyCopy}
          </RichText>
        )}

        {chipRows.length > 0 && (
          <FlexibleHeroBlockStkBkaAttributeChips
            className={TEXT_COLUMN_NEXT_ROW_CLASSES}
            rows={chipRows}
            isCenter={isCenter}
          />
        )}
      </div>
    </FlexibleHeroBlockSection>
  );
};
